<template>
  <div class="font-sans text-neutral-900 antialiased bg-neutral-50 min-h-screen">
    <header v-if="!isPublicReport" class="bg-white/95 backdrop-blur-lg border-b border-neutral-200/60 shadow-lg sticky top-0 z-50 relative no-print">
      <!-- Subtle animated background -->
      <!-- Dekorace má vlastní overflow-hidden; na headeru by ořezávala navigaci -->
      <div class="absolute inset-0 opacity-5 overflow-hidden pointer-events-none">
        <div class="absolute top-2 left-1/3 w-32 h-32 bg-primary-400 rounded-full blur-3xl animate-pulse"></div>
        <div class="absolute top-1 right-1/3 w-24 h-24 bg-red-400 rounded-full blur-2xl animate-pulse" style="animation-delay: 1.5s;"></div>
      </div>
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div class="flex items-center h-16 gap-x-6">
          <div class="flex items-center space-x-3 group relative z-10 flex-shrink-0">
            <div class="relative">
              <img src="@/assets/roundlogov1.png" alt="ScooterPatrol" class="h-11 w-11 rounded-xl shadow-lg transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 ring-2 ring-primary-100/50 group-hover:ring-primary-200" />
              <div class="absolute -inset-1 bg-gradient-to-r from-primary-400/20 to-red-400/20 rounded-xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            </div>
            <div class="relative">
              <!-- Značka aplikace je logo, ne nadpis obsahu — h1 patří
                   titulku stránky, aby byl na každé stránce jediný -->
              <div class="text-xl font-bold text-neutral-800 tracking-tight group-hover:text-neutral-700 transition-colors duration-300">
                ScooterPatrol
              </div>
              <p class="text-xs text-neutral-500 font-medium flex items-center group-hover:text-neutral-600 transition-colors duration-300">
                <svg class="w-3 h-3 mr-1 text-red-500 group-hover:scale-110 transition-transform duration-300" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd" />
                </svg>
                Praha 2
              </p>
            </div>
          </div>
          <nav class="hidden xl:flex items-center space-x-1 nav:space-x-2 ml-auto flex-nowrap">
            <router-link :class="navLinkClasses('/')" to="/" title="Domů" aria-label="Domů">
              <svg class="w-4 h-4 nav:mr-2 transform group-hover:scale-110 group-hover:rotate-12 transition-all duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              <span class="relative hidden nav:inline">
                Domů
                <span class="absolute -bottom-1 left-0 w-0 h-0.5 bg-yellow-400 group-hover:w-full transition-all duration-300"></span>
              </span>
            </router-link>
            <router-link v-if="loggedIn" :class="navLinkClasses('/feed')" to="/feed">
              <svg class="w-4 h-4 mr-2 transform group-hover:scale-110 group-hover:-rotate-6 transition-all duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2h2a2 2 0 002-2z" />
              </svg>
              <span class="relative">
                Tabulka
                <span class="absolute -bottom-1 left-0 w-0 h-0.5 bg-yellow-400 group-hover:w-full transition-all duration-300"></span>
              </span>
            </router-link>
            <router-link v-if="loggedIn" :class="navLinkClasses('/report')" to="/report">
              <svg class="w-4 h-4 mr-2 transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 4h10a2 2 0 012 2v12a2 2 0 01-2 2H7a2 2 0 01-2-2V6a2 2 0 012-2zm2 4h6m-6 4h6m-6 4h4" />
              </svg>
              <span class="relative">
                Interní hlášení
                <span class="absolute -bottom-1 left-0 w-0 h-0.5 bg-yellow-400 group-hover:w-full transition-all duration-300"></span>
              </span>
            </router-link>
            <router-link v-if="loggedIn" :class="navLinkClasses('/okruh')" to="/okruh">
              <svg class="w-4 h-4 mr-2 transform group-hover:scale-110 transition-all duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
              </svg>
              <span class="relative">
                Okruh
                <span class="absolute -bottom-1 left-0 w-0 h-0.5 bg-yellow-400 group-hover:w-full transition-all duration-300"></span>
              </span>
            </router-link>
            <router-link v-if="loggedIn" :class="navLinkClasses('/statistics')" to="/statistics">
              <svg class="w-4 h-4 mr-2 transform group-hover:scale-110 group-hover:rotate-12 transition-all duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2-2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <span class="relative">
                Statistiky
                <span class="absolute -bottom-1 left-0 w-0 h-0.5 bg-yellow-400 group-hover:w-full transition-all duration-300"></span>
              </span>
            </router-link>
            <router-link v-if="loggedIn && isAdmin" :class="navLinkClasses('/parking-prediction')" to="/parking-prediction">
              <svg class="w-4 h-4 mr-2 transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span class="relative">
                Parkovací místa
                <span class="absolute -bottom-1 left-0 w-0 h-0.5 bg-yellow-400 group-hover:w-full transition-all duration-300"></span>
              </span>
            </router-link>
            <router-link v-if="loggedIn && isAdmin" :class="navLinkClasses('/user-management')" to="/user-management">
              <svg class="w-4 h-4 mr-2 transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <span class="relative">
                Správa uživatelů
                <span class="absolute -bottom-1 left-0 w-0 h-0.5 bg-yellow-400 group-hover:w-full transition-all duration-300"></span>
              </span>
            </router-link>
            <router-link v-if="loggedIn && isAdmin" :class="navLinkClasses('/public-reports')" to="/public-reports">
              <svg class="w-4 h-4 mr-2 transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span class="relative">
                Veřejná hlášení
                <span class="absolute -bottom-1 left-0 w-0 h-0.5 bg-yellow-400 group-hover:w-full transition-all duration-300"></span>
              </span>
              <!-- Počet čekajících hlášení — inline, aby nepřekrýval sousední položku -->
              <span v-if="pendingReportsCount > 0" :class="[
                'ml-2 bg-red-500 text-white font-bold rounded-full h-5 min-w-5 px-1.5 flex items-center justify-center shadow-sm border border-white',
                pendingReportsCount > 99 ? 'text-[10px]' : 'text-xs'
              ]">
                {{ pendingReportsCount > 99 ? '99+' : pendingReportsCount }}
              </span>
            </router-link>
            <router-link v-if="loggedIn && isAdmin" :class="navLinkClasses('/settings')" to="/settings" title="Nastavení" aria-label="Nastavení">
              <svg class="w-4 h-4 nav:mr-2 transform group-hover:scale-110 group-hover:rotate-180 transition-all duration-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span class="relative hidden nav:inline">
                Nastavení
                <span class="absolute -bottom-1 left-0 w-0 h-0.5 bg-yellow-400 group-hover:w-full transition-all duration-300"></span>
              </span>
            </router-link>
            <router-link v-if="!loggedIn" :class="navLinkClasses('/sign-in')" to="/sign-in">
              <svg class="w-4 h-4 mr-2 transform group-hover:scale-110 group-hover:translate-x-1 transition-all duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
              </svg>
              <span class="relative">
                Přihlásit se
                <span class="absolute -bottom-1 left-0 w-0 h-0.5 bg-yellow-400 group-hover:w-full transition-all duration-300"></span>
              </span>
            </router-link>
            <button v-if="loggedIn" class="flex items-center px-2 nav:px-3 py-2 text-sm whitespace-nowrap text-neutral-600 hover:text-white hover:bg-red-600 rounded-xl transition-colors duration-200 font-medium group" title="Odhlásit se" aria-label="Odhlásit se" @click.prevent="handleSignOut">
              <svg class="w-4 h-4 nav:mr-2 transform group-hover:scale-110 group-hover:-translate-x-1 transition-all duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span class="relative hidden nav:inline">
                Odhlásit se
                <span class="absolute -bottom-1 left-0 w-0 h-0.5 bg-yellow-400 group-hover:w-full transition-all duration-300"></span>
              </span>
            </button>
          </nav>
         <!-- Mobile menu button -->
          <button @click="mobileMenuOpen = !mobileMenuOpen" class="xl:hidden ml-auto p-2 rounded-xl bg-primary-50 text-primary-600 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-primary-300 ring-1 ring-primary-200 transition-colors">
            <svg v-if="!mobileMenuOpen" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            <svg v-else class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
         <!-- Mobile menu -->
         <div v-if="mobileMenuOpen" class="xl:hidden border-t border-neutral-200/60 bg-white/95 backdrop-blur-md">
           <div class="px-2 pt-2 pb-3 space-y-1">
             <router-link :class="mobileNavLinkClasses('/')" to="/" @click="mobileMenuOpen = false">
               <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                 <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
               </svg>
               Domů
             </router-link>
             <router-link v-if="loggedIn" :class="mobileNavLinkClasses('/feed')" to="/feed" @click="mobileMenuOpen = false">
               <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                 <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2h2a2 2 0 002-2z" />
               </svg>
              Tabulka
              </router-link>
              <router-link v-if="loggedIn" :class="mobileNavLinkClasses('/report')" to="/report" @click="mobileMenuOpen = false">
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 4h10a2 2 0 012 2v12a2 2 0 01-2 2H7a2 2 0 01-2-2V6a2 2 0 012-2zm2 4h6m-6 4h6m-6 4h4" />
                </svg>
                Interní hlášení
              </router-link>
             <router-link v-if="loggedIn" :class="mobileNavLinkClasses('/okruh')" to="/okruh" @click="mobileMenuOpen = false">
               <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                 <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
               </svg>
               Okruh
             </router-link>
             <router-link v-if="loggedIn" :class="mobileNavLinkClasses('/statistics')" to="/statistics" @click="mobileMenuOpen = false">
               <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                 <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2-2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
               </svg>
               Statistiky
             </router-link>
             <router-link v-if="loggedIn && isAdmin" :class="mobileNavLinkClasses('/parking-prediction')" to="/parking-prediction" @click="mobileMenuOpen = false">
               <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                 <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                 <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
               </svg>
               Parkovací místa
             </router-link>
             <router-link v-if="loggedIn && isAdmin" :class="mobileNavLinkClasses('/user-management')" to="/user-management" @click="mobileMenuOpen = false">
               <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                 <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
               </svg>
               Správa uživatelů
             </router-link>
             <router-link v-if="loggedIn && isAdmin" :class="mobileNavLinkClasses('/public-reports')" to="/public-reports" @click="mobileMenuOpen = false">
               <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                 <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
               </svg>
               <span class="flex-1">Veřejná hlášení</span>
               <!-- Červené kolečko s počtem pending hlášení pro mobilní -->
               <span v-if="pendingReportsCount > 0" :class="[
                 'bg-red-500 text-white font-bold rounded-full h-5 min-w-5 px-1.5 flex items-center justify-center shadow-sm ml-2 border border-white',
                 pendingReportsCount > 99 ? 'text-xs' : 'text-base'
               ]">
                 {{ pendingReportsCount > 99 ? '99+' : pendingReportsCount }}
               </span>
             </router-link>
             <router-link v-if="loggedIn && isAdmin" :class="mobileNavLinkClasses('/settings')" to="/settings" @click="mobileMenuOpen = false">
               <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                 <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                 <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
               </svg>
               Nastavení
             </router-link>
             <router-link v-if="!loggedIn" :class="mobileNavLinkClasses('/sign-in')" to="/sign-in" @click="mobileMenuOpen = false">
               <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                 <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
               </svg>
               Přihlásit se
             </router-link>
             <button v-if="loggedIn" class="flex items-center w-full text-left px-3 py-2 text-neutral-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200 font-medium" @click="handleSignOut">
               <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                 <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
               </svg>
               Odhlásit se
             </button>
           </div>
         </div>
      </div>
    </header>
    <main class="flex-1">
      <router-view :loggedIn="loggedIn" :isAdmin="isAdmin"/>
    </main>
    <portal-target name="modals" multiple></portal-target>
  </div>
</template>

<script setup>
 /**
  * Main Vue application component that contains the navigation panel and router-view.
  * Router-view displays components based on the current route.
  * This script defines reactive variables for tracking login status and user role.
  * The handleSignOut function is defined for user logout.
  */

  import { ref, watchEffect, onMounted, onUnmounted } from "vue";
  import { useRouter } from "vue-router";
  import { useStore } from 'vuex';
  import { useRoute } from 'vue-router';
  import api, { refreshTokenIfNeeded } from '@/api';

  const router = useRouter();
  const store = useStore();
  const loggedIn = ref(store.state.loggedIn);
  const isAdmin = ref(store.state.isAdmin);
  const mobileMenuOpen = ref(false);
  const pendingReportsCount = ref(0);
  let intervalId = null;

  const route = useRoute();
  const isPublicReport = ref(route.path === '/public-report');
  const navLinkClasses = (path) => `px-2 nav:px-3 py-2 text-sm whitespace-nowrap rounded-xl font-medium transition-colors duration-200 flex items-center relative group ${
      route.path === path 
        ? 'text-primary-700 bg-primary-50 ring-1 ring-primary-200'
        : 'text-neutral-600 hover:text-primary-600 hover:bg-primary-50'
    }`;
    
    const mobileNavLinkClasses = (path) => `px-4 py-3 rounded-xl font-medium transition-colors duration-200 flex items-center relative ${
      route.path === path 
        ? 'text-primary-700 bg-primary-50'
        : 'text-neutral-600 hover:text-primary-600 hover:bg-primary-50'
    }`;

  // Funkce pro načítání počtu pending hlášení
  const fetchPendingReportsCount = async () => {
    if (!loggedIn.value || !isAdmin.value) {
      pendingReportsCount.value = 0;
      return;
    }

    try {
      const response = await api.get('/public-reports?status=pending');
      pendingReportsCount.value = response.data.length;
    } catch (error) {
      console.error('Chyba při načítání počtu pending hlášení:', error);
      pendingReportsCount.value = 0;
    }
  };

  // Sledovat změny store a aktualizovat počet pending hlášení
  watchEffect(() => {
    loggedIn.value = store.state.loggedIn;
    isAdmin.value = store.state.isAdmin;
    isPublicReport.value = route.path === '/public-report';
    fetchPendingReportsCount();
  });

  // Lifecycle hooks
  onMounted(() => {
    // Nastavit interval pro automatické obnovování každých 30 sekund
    // Token platí 24 h a brigádnice hlásí celý den — hodinu před vypršením
    // se tiše obnoví, aby přihlášení nevypršelo uprostřed hlášení.
    if (loggedIn.value) refreshTokenIfNeeded();
    intervalId = setInterval(() => {
      if (loggedIn.value) refreshTokenIfNeeded();
      if (loggedIn.value && isAdmin.value) {
        fetchPendingReportsCount();
      }
    }, 30000);
  });

  onUnmounted(() => {
    if (intervalId) {
      clearInterval(intervalId);
    }
  });

  const handleSignOut = () => {
    store.commit('setLoggedIn', false);
    store.commit('setRole', null);
    localStorage.removeItem('role');
    mobileMenuOpen.value = false;
    router.push("/");
  };


</script>

<style>
@media print {
  .no-print {
    display: none !important;
  }
  
  /* Ensure proper display when printing */
  body {
    margin: 0;
    padding: 0;
    background: white !important;
  }
  
  /* Skrýt všechny animace a efekty při tisku */
  * {
    animation: none !important;
    transition: none !important;
    transform: none !important;
    box-shadow: none !important;
  }
}
</style>
